import React from 'react';
import Autocomplete from '@mui/material/Autocomplete';
import CircularProgress from '@mui/material/CircularProgress';
import TextField from '@mui/material/TextField';
import { toOptionIdentity, toOptionLabel, toOptionValue, useGroupedOptions } from './autocompleteHelpers';

/**
 * The MUI Autocomplete behind every react-widgets DropdownList: a list you pick from, with
 * type-to-filter. Not clearable by default because the widget carried no clear affordance; pass
 * `clearable` for the ×, which reports the clear as `onChange(null)`. No `freeSolo` because it
 * never accepted text that was not an option.
 *
 * The stored value may be the option object or just its key — a `defaultValue` is usually the raw
 * one — so it is resolved back to the option before MUI sees it, which also stops MUI warning
 * about a value that matches none of the options.
 */
/**
 * @param {{
 *   id?: string,
 *   name?: string,
 *   options?: any[],
 *   value?: any,
 *   onChange?: (selected: any) => void,
 *   onBlur?: () => void,
 *   textField?: string | ((option: any) => any),
 *   valueField?: string,
 *   placeholder?: string,
 *   disabled?: boolean,
 *   clearable?: boolean,
 *   busy?: boolean,
 *   error?: boolean,
 *   groupBy?: string | ((option: any) => any),
 *   renderListGroup?: (params: { group: any }) => React.ReactNode,
 *   sx?: object,
 *   slotProps?: object,
 * }} props
 */
export default function DropdownAutocomplete({
  id,
  name,
  options,
  value,
  onChange,
  onBlur,
  textField,
  valueField,
  placeholder,
  disabled,
  clearable = false,
  busy = false,
  error = false,
  groupBy,
  renderListGroup,
  sx,
  // Passed straight to MUI. The SPA preview needs `popper` props: its option list portals out
  // to <body>, and the floating window that hosts it treats any mousedown as a drag start.
  slotProps,
}) {
  const getLabel = toOptionLabel(textField);
  const grouping = useGroupedOptions(options, groupBy, renderListGroup);
  const current = toOptionValue(grouping.options, valueField, value);

  return (
    <Autocomplete
      disableClearable={!clearable}
      autoHighlight
      id={id}
      {...grouping}
      value={current}
      disabled={disabled}
      loading={busy}
      getOptionLabel={getLabel}
      {...toOptionIdentity(valueField)}
      onChange={(_event, selected) => onChange(selected)}
      onBlur={onBlur}
      sx={sx}
      slotProps={slotProps}
      renderInput={(params) => (
        <TextField
          {...params}
          name={name}
          placeholder={placeholder}
          error={error}
          slotProps={{
            ...params.slotProps,
            input: {
              ...params.slotProps.input,
              endAdornment: (
                <>
                  {busy && <CircularProgress size={16} sx={{ mr: 1 }} />}
                  {params.slotProps.input.endAdornment}
                </>
              ),
            },
          }}
        />
      )}
    />
  );
}
